import { ChangeEvent, DragEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as maptalks from "maptalks";
import "maptalks/dist/maptalks.css";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { subscribeToOfflineMapProgress, useAppContext } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import {
  OPENSTREETMAP_TILE_PROXY_PATH,
  OPENSTREETMAP_TILE_URL,
  appendMapTileAccessToken
} from "../map/tileLayers";
import LevelSelector from "./LevelSelector";

type BoundingBox = {
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
};

type StoredBoundingBox = {
  zoom: number;
  boundingBox?: BoundingBox | null;
};

type StoredMapView = {
  center: [number, number];
  zoom: number;
};

type ToolbarButtonKey = "draw" | "clear";

type ProgressStatus = "idle" | "running" | "completed" | "error" | "canceled";

const DEFAULT_BOUNDING_BOX: BoundingBox = {
  minLat: 44.38,
  minLon: 25.92,
  maxLat: 44.58,
  maxLon: 26.2
};

const DEFAULT_MAP_VIEW: StoredMapView = {
  center: [26.0, 44.47],
  zoom: 11
};

const MAP_VIEW_STORAGE_KEY = "skyview.offlineMap.view";

const ZOOM_LEVEL_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#00fa36ff",
  "#637939",
  "#8c6d31",
  "#843c39",
  "#f800d7ff",
  "#003cffff"
];

const clampZoomLevel = (zoom: number) => {
  if (!Number.isFinite(zoom)) {
    return 0;
  }
  return Math.max(0, Math.min(Math.round(zoom), ZOOM_LEVEL_COLORS.length - 1));
};

const getZoomColor = (zoom: number) => ZOOM_LEVEL_COLORS[clampZoomLevel(zoom)];

const sanitizeMapView = (view: Partial<StoredMapView> | null | undefined): StoredMapView | null => {
  if (!view) {
    return null;
  }
  const center = Array.isArray(view.center) ? view.center : null;
  const zoomValue = typeof view.zoom === "number" ? view.zoom : Number(view.zoom);
  if (!center || center.length !== 2) {
    return null;
  }
  const [lon, lat] = [Number(center[0]), Number(center[1])];
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(zoomValue)) {
    return null;
  }
  return { center: [lon, lat], zoom: Math.max(0, zoomValue) };
};

const loadStoredMapView = (): StoredMapView | null => {
  if (typeof window === "undefined") {
    return null;
  }
  const raw = window.localStorage.getItem(MAP_VIEW_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return sanitizeMapView(JSON.parse(raw));
  } catch (_error) {
    return null;
  }
};

const getInitialMapView = (): StoredMapView => loadStoredMapView() ?? DEFAULT_MAP_VIEW;

const persistMapView = (view: StoredMapView) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(MAP_VIEW_STORAGE_KEY, JSON.stringify(view));
  } catch (_error) {
    // Ignore storage errors
  }
};

// Use a drag-based rectangle mode so users can keep the left mouse button pressed while drawing.
const DRAW_MODE = "freeHandRectangle";

const colorWithAlpha = (hex: string, alpha: number) => {
  let normalized = hex.replace("#", "");
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((char) => char + char)
      .join("");
  }
  if (normalized.length !== 6) {
    return `rgba(0, 0, 0, ${alpha})`;
  }
  const value = Number.parseInt(normalized, 16);
  if (Number.isNaN(value)) {
    return `rgba(0, 0, 0, ${alpha})`;
  }
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const boundingBoxToPolygonCoords = (boundingBox: BoundingBox): [number, number][] => [
  [boundingBox.minLon, boundingBox.maxLat],
  [boundingBox.maxLon, boundingBox.maxLat],
  [boundingBox.maxLon, boundingBox.minLat],
  [boundingBox.minLon, boundingBox.minLat],
  [boundingBox.minLon, boundingBox.maxLat]
];

const isValidBoundingBox = (value: unknown): value is BoundingBox => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const maybe = value as Partial<BoundingBox>;
  return (
    typeof maybe.minLat === "number" &&
    typeof maybe.minLon === "number" &&
    typeof maybe.maxLat === "number" &&
    typeof maybe.maxLon === "number"
  );
};

const parseContentDispositionFilename = (headerValue: string | null) => {
  if (!headerValue) {
    return null;
  }
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(headerValue);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (_error) {
      // Fall back to plain filename parsing
    }
  }
  const basicMatch = /filename=\"?([^\";]+)\"?/i.exec(headerValue);
  return basicMatch?.[1] ?? null;
};

const buildDefaultArchiveName = () => {
  const now = new Date();
  const timeStamp = now.toISOString().replace(/[:.]/g, "-");
  return `offline-map-${timeStamp}.zip`;
};

const triggerBlobDownload = (blob: Blob, suggestedName: string | null) => {
  if (typeof document === "undefined") {
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = suggestedName?.trim() || buildDefaultArchiveName();
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
};

const isAbortError = (error: unknown) => {
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    return error.name === "AbortError";
  }
  return error instanceof Error && error.name === "AbortError";
};

type ToolbarCallbacks = {
  onDraw: () => void;
  onClear: () => void;
};

type OfflineMapCfgDlgProps = {
  open: boolean;
  onClose: () => void;
  downloadingEnabled?: boolean;
};

const OfflineMapCfgDlg = ({ open, onClose, downloadingEnabled = true }: OfflineMapCfgDlgProps) => {
  const { authorizedFetch, resolveApiPath, tokens } = useAuth();
  const { connectionId } = useAppContext();
  const openStreetMapTileUrl = useMemo(
    () => appendMapTileAccessToken(resolveApiPath(OPENSTREETMAP_TILE_PROXY_PATH), tokens?.accessToken),
    [resolveApiPath, tokens?.accessToken]
  );
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maptalks.Map | null>(null);
  const selectionLayerRef = useRef<maptalks.VectorLayer | null>(null);
  const existingBoundsLayerRef = useRef<maptalks.VectorLayer | null>(null);
  const drawToolRef = useRef<maptalks.DrawTool | null>(null);
  const toolbarRef = useRef<maptalks.control.Toolbar | null>(null);
  const toolbarButtonsRef = useRef<Record<ToolbarButtonKey, HTMLLIElement | null>>({
    draw: null,
    clear: null
  });
  const toolbarCallbacksRef = useRef<ToolbarCallbacks>({
    onDraw: () => {},
    onClear: () => {}
  });
  const geometryDetachRef = useRef<(() => void) | null>(null);
  const selectedGeometryRef = useRef<maptalks.Geometry | null>(null);
  const [downloadRequestId, setDownloadRequestId] = useState<string | null>(null);
  const downloadRequestIdRef = useRef<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState<ProgressStatus>("idle");
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [selectedBounds, setSelectedBounds] = useState<BoundingBox | null>(null);
  const selectedBoundsRef = useRef<BoundingBox | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const isDrawingRef = useRef(isDrawing);
  const [cancelPending, setCancelPending] = useState(false);
  const [mapZoom, setMapZoom] = useState<number>(() => getInitialMapView().zoom);
  const mapZoomRef = useRef<number>(mapZoom);
  const mapDraggableRef = useRef<boolean | null>(null);
  const [mapViewBounds, setMapViewBounds] = useState<BoundingBox | null>(null);
  const mapViewBoundsRef = useRef<BoundingBox | null>(null);
  const [storedBounds, setStoredBounds] = useState<StoredBoundingBox[]>([]);
  const [boundsLoadError, setBoundsLoadError] = useState<string | null>(null);
  const [boundsRefreshIndex, setBoundsRefreshIndex] = useState(0);
  const [selectedZoomLevels, setSelectedZoomLevels] = useState<number[]>([]);
  const [activeZoomLevel, setActiveZoomLevel] = useState<number | null>(null);
  const [offlineSizeBytes, setOfflineSizeBytes] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<ProgressStatus>("idle");
  const [exportProgress, setExportProgress] = useState(0);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportTotalBytes, setExportTotalBytes] = useState<number | null>(null);
  const exportAbortControllerRef = useRef<AbortController | null>(null);
  const [importStatus, setImportStatus] = useState<ProgressStatus>("idle");
  const [importProgress, setImportProgress] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importDragActive, setImportDragActive] = useState(false);
  const [importingFileName, setImportingFileName] = useState<string | null>(null);
  const [importRequestId, setImportRequestId] = useState<string | null>(null);
  const importRequestIdRef = useRef<string | null>(null);
  const importAbortControllerRef = useRef<AbortController | null>(null);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);

  const persistCurrentMapView = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    const center = map.getCenter();
    const zoom = map.getZoom();
    if (!center || typeof zoom !== "number" || Number.isNaN(zoom)) {
      return;
    }
    const sanitized = sanitizeMapView({ center: [center.x, center.y], zoom });
    if (sanitized) {
      persistMapView(sanitized);
    }
  }, []);

  const disableMapDragging = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (mapDraggableRef.current === null) {
      mapDraggableRef.current = Boolean(map.options?.draggable);
    }
    map.config("draggable", false);
  }, []);

  const restoreMapDragging = useCallback(() => {
    const map = mapRef.current;
    if (mapDraggableRef.current !== null && map) {
      map.config("draggable", mapDraggableRef.current);
    }
    mapDraggableRef.current = null;
  }, []);

  const updateToolbarButtonsDisabledState = useCallback(() => {
    const state: Record<ToolbarButtonKey, boolean> = {
      draw: isDrawingRef.current,
      clear: !selectedBoundsRef.current
    };
    (Object.entries(state) as Array<[ToolbarButtonKey, boolean]>).forEach(([key, disabled]) => {
      const button = toolbarButtonsRef.current[key];
      if (button) {
        button.style.opacity = disabled ? "0.4" : "1";
        button.style.pointerEvents = disabled ? "none" : "auto";
      }
    });
  }, []);

  const updateBoundsFromGeometry = useCallback((geometry: maptalks.Geometry | null) => {
    if (!geometry) {
      selectedBoundsRef.current = null;
      setSelectedBounds(null);
      updateToolbarButtonsDisabledState();
      return;
    }
    const extent = geometry.getExtent();
    if (!extent || extent.ymin == null || extent.xmin == null || extent.ymax == null || extent.xmax == null) {
      selectedBoundsRef.current = null;
      setSelectedBounds(null);
      updateToolbarButtonsDisabledState();
      return;
    }
    const next: BoundingBox = {
      minLat: extent.ymin,
      minLon: extent.xmin,
      maxLat: extent.ymax,
      maxLon: extent.xmax
    };
    selectedBoundsRef.current = next;
    setSelectedBounds(next);
    updateToolbarButtonsDisabledState();
  }, [updateToolbarButtonsDisabledState]);
  const detachGeometry = useCallback(() => {
    geometryDetachRef.current?.();
    geometryDetachRef.current = null;
    const geometry = selectedGeometryRef.current;
    if (geometry) {
      geometry.remove();
    }
    selectedGeometryRef.current = null;
    updateBoundsFromGeometry(null);
  }, [updateBoundsFromGeometry]);

  const attachGeometry = useCallback((geometry: maptalks.Geometry) => {
    detachGeometry();
    const selectionLayer = selectionLayerRef.current;
    if (!selectionLayer) {
      return;
    }
    geometry.setSymbol({
      lineColor: "#1e88e5",
      lineWidth: 2,
      polygonFill: "rgba(30,136,229,0.15)",
      polygonOpacity: 0.4
    });
    selectionLayer.addGeometry(geometry);
    if (geometry.startEdit) {
      geometry.startEdit({ fixAspectRatio: false });
    }
    const handleShapeChange = () => updateBoundsFromGeometry(geometry);
    geometry.on("shapechange", handleShapeChange);
    geometry.on("positionchange", handleShapeChange);
    geometryDetachRef.current = () => {
      geometry.off("shapechange", handleShapeChange);
      geometry.off("positionchange", handleShapeChange);
      if (geometry.endEdit) {
        geometry.endEdit();
      }
    };
    selectedGeometryRef.current = geometry;
    updateBoundsFromGeometry(geometry);
  }, [detachGeometry, updateBoundsFromGeometry]);

  const clearSelection = useCallback(() => {
    detachGeometry();
    selectionLayerRef.current?.clear();
  }, [detachGeometry]);

  const updateMapZoom = useCallback((value: number | undefined) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return;
    }
    mapZoomRef.current = value;
    setMapZoom(value);
  }, []);

  const updateMapViewBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      mapViewBoundsRef.current = null;
      setMapViewBounds(null);
      return;
    }
    const extent = map.getExtent();
    if (!extent || extent.ymin == null || extent.xmin == null || extent.ymax == null || extent.xmax == null) {
      mapViewBoundsRef.current = null;
      setMapViewBounds(null);
      return;
    }
    const bounds: BoundingBox = {
      minLat: extent.ymin,
      minLon: extent.xmin,
      maxLat: extent.ymax,
      maxLon: extent.xmax
    };
    mapViewBoundsRef.current = bounds;
    setMapViewBounds(bounds);
  }, []);

  const renderStoredBounds = useCallback(() => {
    const layer = existingBoundsLayerRef.current;
    if (!layer) {
      return;
    }
    layer.clear();
    storedBounds.forEach(({ boundingBox, zoom }) => {
      if (!boundingBox) {
        return;
      }
      const threshold = mapZoomRef.current ?? 0;
      const coords = boundingBoxToPolygonCoords(boundingBox);
      const color = getZoomColor(zoom);
      const polygon = new maptalks.Polygon(coords, {
        symbol: {
          lineColor: color,
          lineWidth: 1,
          polygonFill: colorWithAlpha(color, 0.3),
          polygonOpacity: 0.4
        }
      });
      polygon.setProperties({ zoom });
      if (zoom <= threshold) {
        polygon.hide();
      }
      polygon.on("click", () => {
        if (zoom <= (mapZoomRef.current ?? 0)) {
          return;
        }
        setSelectedZoomLevels((prev) => {
          const next = Array.from(new Set([...prev, zoom])).sort((a, b) => a - b);
          return next;
        });
        setActiveZoomLevel(zoom);
        const extent = polygon.getExtent();
        if (extent && mapRef.current) {
          mapRef.current.fitExtent(extent, 20);
        }
        const selectionGeometry = new maptalks.Polygon(coords);
        attachGeometry(selectionGeometry);
      });
      layer.addGeometry(polygon);
    });
  }, [attachGeometry, storedBounds]);


  const setDrawingState = useCallback(
    (value: boolean) => {
      isDrawingRef.current = value;
      setIsDrawing(value);
      updateToolbarButtonsDisabledState();
    },
    [updateToolbarButtonsDisabledState]
  );

  useEffect(() => {
    downloadRequestIdRef.current = downloadRequestId;
  }, [downloadRequestId]);

  useEffect(() => {
    importRequestIdRef.current = importRequestId;
  }, [importRequestId]);

  useEffect(() => {
    if (downloadStatus !== "running") {
      setCancelPending(false);
    }
  }, [downloadStatus]);

  useEffect(() => {
    setSelectedZoomLevels((prev) => {
      const filtered = prev.filter((level) => level > mapZoom);
      if (filtered.length === prev.length && filtered.every((level, index) => level === prev[index])) {
        return prev;
      }
      return filtered;
    });
  }, [mapZoom]);

  useEffect(() => {
    setActiveZoomLevel((prev) => {
      if (prev !== null && selectedZoomLevels.includes(prev)) {
        return prev;
      }
      return selectedZoomLevels[selectedZoomLevels.length - 1] ?? null;
    });
  }, [selectedZoomLevels]);

  useEffect(() => {
    if (!open) {
      setStoredBounds([]);
      return;
    }
    let cancelled = false;
    const fetchBounds = async () => {
      try {
        const response = await authorizedFetch("/api/map/offline/bounds");
        if (!response.ok) {
          throw new Error(`Server responded ${response.status}`);
        }
        const payload = (await response.json().catch(() => null)) as {
          levels?: Array<{ zoom?: number; boundingBox?: BoundingBox | null }>;
        } | null;
        if (cancelled) {
          return;
        }
        if (!payload?.levels) {
          setStoredBounds([]);
          setBoundsLoadError(null);
          return;
        }
        const mapped = payload.levels
          .filter((entry) => typeof entry.zoom === "number")
          .map((entry) => ({
            zoom: Number(entry.zoom),
            boundingBox: entry.boundingBox ?? null
          }));
        setStoredBounds(mapped);
        setBoundsLoadError(null);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load offline bounds", error);
          setStoredBounds([]);
          setBoundsLoadError(error instanceof Error ? error.message : "Unable to load offline bounds");
        }
      }
    };
    void fetchBounds();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, boundsRefreshIndex, open]);

  useEffect(() => {
    renderStoredBounds();
  }, [renderStoredBounds]);

  useEffect(() => {
    const layer = existingBoundsLayerRef.current;
    if (!layer) {
      return;
    }
    const threshold = mapZoomRef.current ?? mapZoom;
    layer.getGeometries().forEach((geometry) => {
      const zoomValue = Number(geometry.getProperties()?.zoom);
      if (Number.isNaN(zoomValue)) {
        geometry.show();
        return;
      }
      if (zoomValue <= threshold) {
        geometry.hide();
      } else {
        geometry.show();
      }
    });
  }, [mapZoom]);

  useEffect(() => {
    return () => {
      exportAbortControllerRef.current?.abort();
      importAbortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!open || !mapContainerRef.current || mapRef.current) {
      return;
    }

    const initialMapView = getInitialMapView();
    const map = new maptalks.Map(mapContainerRef.current, {
      center: initialMapView.center,
      zoom: initialMapView.zoom,
      centerCross: false,
      baseLayer: new maptalks.TileLayer("base", {
        urlTemplate: openStreetMapTileUrl
      }),
      zoomControl: true
    });
    mapRef.current = map;

    const existingLayer = new maptalks.VectorLayer("existing-bounds").addTo(map);
    existingBoundsLayerRef.current = existingLayer;
    const selectionLayer = new maptalks.VectorLayer("selection").addTo(map);
    selectionLayerRef.current = selectionLayer;

    const drawTool = new maptalks.DrawTool({
      mode: DRAW_MODE,
      once: true,
      symbol: {
        lineColor: "#1e88e5",
        lineWidth: 2,
        polygonFill: "rgba(30,136,229,0.15)",
        polygonOpacity: 0.4
      }
    });
    drawToolRef.current = drawTool;
    drawTool.addTo(map);
    drawTool.disable();

    const handleDrawStart = () => setDrawingState(true);
    const handleDrawEnd = (event: any) => {
      setDrawingState(false);
      drawTool.disable();
      restoreMapDragging();
      if (event?.geometry) {
        attachGeometry(event.geometry);
      }
    };
    const handleDrawCancel = () => {
      setDrawingState(false);
      drawTool.disable();
      restoreMapDragging();
    };

    drawTool.on("drawstart", handleDrawStart);
    drawTool.on("drawend", handleDrawEnd);
    drawTool.on("drawcancel", handleDrawCancel);

    updateMapZoom(map.getZoom());
    updateMapViewBounds();

    const handleZoomEnd = () => {
      updateMapZoom(map.getZoom());
      updateMapViewBounds();
      persistCurrentMapView();
    };
    const handleMoveEnd = () => {
      updateMapViewBounds();
      persistCurrentMapView();
    };

    map.on("zoomend", handleZoomEnd);
    map.on("moveend", handleMoveEnd);

    requestAnimationFrame(() => {
      map.checkSize();
    });

    renderStoredBounds();

    const toolbarConfig: Array<{ key: ToolbarButtonKey; label: string; handler: () => void }> = [
      { key: "draw", label: "Draw", handler: () => toolbarCallbacksRef.current.onDraw() },
      { key: "clear", label: "Clear", handler: () => toolbarCallbacksRef.current.onClear() }
    ];
    const toolbar = new maptalks.control.Toolbar({
      position: { top: 12, right: 12 },
      vertical: true,
      items: toolbarConfig.map(({ label, handler }) => ({
        item: `<span>${label}</span>`,
        click: () => handler()
      }))
    });
    toolbar.addTo(map);
    toolbarRef.current = toolbar;
    const toolbarDom = toolbar.getDOM();
    if (toolbarDom) {
      const buttons = toolbarDom.querySelectorAll("li");
      toolbarConfig.forEach(({ key }, index) => {
        toolbarButtonsRef.current[key] = buttons[index] ?? null;
      });
      updateToolbarButtonsDisabledState();
    }

    return () => {
      drawTool.off("drawstart", handleDrawStart);
      drawTool.off("drawend", handleDrawEnd);
      drawTool.off("drawcancel", handleDrawCancel);
      map.off("zoomend", handleZoomEnd);
      map.off("moveend", handleMoveEnd);
      toolbarRef.current?.remove();
      toolbarRef.current = null;
      toolbarButtonsRef.current = {
        draw: null,
        clear: null
      };
      drawTool.disable();
      drawTool.remove();
      drawToolRef.current = null;
      if (existingBoundsLayerRef.current) {
        existingBoundsLayerRef.current.remove();
        existingBoundsLayerRef.current = null;
      }
      selectionLayer.remove();
      selectionLayerRef.current = null;
      map.remove();
      mapRef.current = null;
      clearSelection();
      setDrawingState(false);
      restoreMapDragging();
    };
  }, [
    attachGeometry,
    clearSelection,
    persistCurrentMapView,
    open,
    openStreetMapTileUrl,
    renderStoredBounds,
    restoreMapDragging,
    updateMapViewBounds,
    updateMapZoom,
    updateToolbarButtonsDisabledState,
    setDrawingState
  ]);

  const generateDownloadRequestId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `offline-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  };

  const handleOfflineMapDownload = async () => {
    if (!downloadingEnabled || downloadStatus === "running") {
      return;
    }
    const zoomLevels = Array.from(new Set(selectedZoomLevels.map((level) => clampZoomLevel(level)))).sort((a, b) => a - b);
    if (zoomLevels.length === 0) {
      setDownloadError("Select at least one zoom level.");
      return;
    }
    // Clear stale export state so progress reflects the new download.
    setExportStatus("idle");
    setExportProgress(0);
    setExportError(null);
    setExportTotalBytes(null);
    const requestId = generateDownloadRequestId();
    setDownloadRequestId(requestId);
    downloadRequestIdRef.current = requestId;
    setDownloadProgress(0);
    setDownloadStatus("running");
    setDownloadError(null);
    const boundingBox = selectedBoundsRef.current ?? mapViewBoundsRef.current ?? DEFAULT_BOUNDING_BOX;
    const payload: Record<string, unknown> = {
      url: OPENSTREETMAP_TILE_URL,
      zoomLevels,
      boundingBox,
      requestId
    };
    if (connectionId) {
      payload.connectionId = connectionId;
    }
    try {
      const response = await authorizedFetch("/api/map/offline/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(`Server responded ${response.status}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to start offline map download";
      setDownloadStatus("error");
      setDownloadError(message);
    }
  };

  const handleCancelOfflineMapDownload = async () => {
    const requestId = downloadRequestIdRef.current;
    if (!requestId) {
      return;
    }
    setCancelPending(true);
    setDownloadError(null);
    try {
      const response = await authorizedFetch("/api/map/offline/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId })
      });
      if (!response.ok) {
        if (response.status === 404) {
          setDownloadStatus("idle");
          setDownloadRequestId(null);
          return;
        }
        throw new Error(`Server responded ${response.status}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to cancel offline map download";
      setDownloadStatus("error");
      setDownloadError(message);
    } finally {
      setCancelPending(false);
    }
  };

  const handleDrawRectangle = useCallback(() => {
    if (!open || isDrawing) {
      return;
    }
    const drawTool = drawToolRef.current;
    if (!drawTool) {
      return;
    }
    selectionLayerRef.current?.clear();
    setDrawingState(true);
    drawTool.setMode(DRAW_MODE);
    disableMapDragging();
    drawTool.enable();
  }, [disableMapDragging, isDrawing, open]);

  const handleClearSelection = useCallback(() => {
    const drawTool = drawToolRef.current;
    if (drawTool) {
      drawTool.disable();
    }
    setDrawingState(false);
    restoreMapDragging();
    clearSelection();
  }, [clearSelection, restoreMapDragging]);

  const resetSelectionState = useCallback(() => {
    handleClearSelection();
    setSelectedZoomLevels([]);
    setActiveZoomLevel(null);
  }, [handleClearSelection]);


  const handleZoomLevelsChange = (levels: number[], toggledLevel: number, checked: boolean) => {
    setSelectedZoomLevels(levels);
    setActiveZoomLevel(checked ? toggledLevel : levels[levels.length - 1] ?? null);
  };

  useEffect(() => {
    if (!open) {
      setDownloadStatus("idle");
      setDownloadError(null);
      setDownloadProgress(0);
      setDownloadRequestId(null);
      setDeleteDialogOpen(false);
      setDeletePending(false);
      setDeleteError(null);
      resetSelectionState();
      setCancelPending(false);
      exportAbortControllerRef.current?.abort();
      exportAbortControllerRef.current = null;
      setExportStatus("idle");
      setExportProgress(0);
      setExportError(null);
      setExportTotalBytes(null);
      importAbortControllerRef.current?.abort();
      importAbortControllerRef.current = null;
      setImportStatus("idle");
      setImportProgress(0);
      setImportError(null);
      setImportRequestId(null);
      setImportDialogOpen(false);
      setImportingFileName(null);
      setImportDragActive(false);
      restoreMapDragging();
    }
  }, [open, resetSelectionState, restoreMapDragging]);

  useEffect(() => {
    type OfflineMapProgressData = {
      requestId?: unknown;
      progress?: unknown;
      status?: unknown;
      error?: unknown;
      operation?: unknown;
    };
    const unsubscribe = subscribeToOfflineMapProgress((message) => {
      const data = (message.data ?? {}) as OfflineMapProgressData;
      const requestId = typeof data.requestId === "string" ? data.requestId : null;
      const progress = typeof data.progress === "number" ? data.progress : null;
      const statusRaw = typeof data.status === "string" ? data.status : null;
      const operation = typeof data.operation === "string" ? data.operation : "download";

      if (operation === "import") {
        if (!requestId || requestId !== importRequestIdRef.current) {
          return;
        }
        if (progress !== null) {
          setImportProgress(Math.min(100, Math.max(0, progress)));
        }
        if (statusRaw === "error") {
          setImportStatus("error");
          const messageText =
            typeof message.data?.error === "string" && message.data.error.trim().length > 0
              ? message.data.error
              : "Offline map import failed";
          setImportError(messageText);
          setImportRequestId(null);
        } else if (statusRaw === "completed") {
          setImportStatus("completed");
          setImportError(null);
          setImportProgress(100);
          setImportRequestId(null);
          setImportDialogOpen(false);
          setImportingFileName(null);
        } else if (statusRaw === "canceled") {
          setImportStatus("canceled");
          setImportError(null);
          setImportRequestId(null);
        } else {
          setImportStatus("running");
        }
        return;
      }

      if (!requestId || requestId !== downloadRequestIdRef.current) {
        return;
      }
      if (progress !== null) {
        setDownloadProgress(Math.min(100, Math.max(0, progress)));
      }
      if (statusRaw === "error") {
        setDownloadStatus("error");
        const messageText =
          typeof message.data?.error === "string" && message.data.error.trim().length > 0
            ? message.data.error
            : "Offline map download failed";
        setDownloadError(messageText);
        setDownloadRequestId(null);
      } else if (statusRaw === "completed") {
        setDownloadStatus("completed");
        setDownloadError(null);
        setDownloadProgress(100);
        setDownloadRequestId(null);
        resetSelectionState();
        setBoundsRefreshIndex((prev) => prev + 1);
      } else if (statusRaw === "canceled") {
        setDownloadStatus("canceled");
        setDownloadError(null);
        setDownloadRequestId(null);
      } else {
        setDownloadStatus("running");
      }
    });
    return unsubscribe;
  }, [resetSelectionState]);

  useEffect(() => {
    toolbarCallbacksRef.current = {
      onDraw: handleDrawRectangle,
      onClear: handleClearSelection
    };
  }, [handleClearSelection, handleDrawRectangle]);

  const hasSelection = Boolean(selectedBounds);
  const hasLevelSelection = selectedZoomLevels.length > 0;
  const isDownloadRunning = downloadStatus === "running";
  const isExportRunning = exportStatus === "running";
  const isImportRunning = importStatus === "running";
  const fetchOfflineMapSize = useCallback(async () => {
    if (!open) {
      return;
    }
    try {
      const response = await authorizedFetch("/api/map/offline/size");
      if (!response.ok) {
        throw new Error(`Server responded ${response.status}`);
      }
      const payload = (await response.json().catch(() => null)) as { sizeBytes?: unknown } | null;
      const valueRaw = payload?.sizeBytes;
      const parsed = typeof valueRaw === "number" ? valueRaw : Number(valueRaw);
      setOfflineSizeBytes(Number.isFinite(parsed) ? parsed : null);
    } catch (error) {
      console.error("Failed to load offline map size", error);
      setOfflineSizeBytes(null);
    }
  }, [authorizedFetch, open]);

  const handleDeleteOfflineMaps = useCallback(async () => {
    setDeleteDialogOpen(false);
    setDeletePending(true);
    setDeleteError(null);
    try {
      const response = await authorizedFetch("/api/map/offline/content", { method: "DELETE" });
      if (!response.ok) {
        throw new Error(`Server responded ${response.status}`);
      }
      handleClearSelection();
      setStoredBounds([]);
      setSelectedZoomLevels([]);
      setActiveZoomLevel(null);
      setDownloadStatus("idle");
      setDownloadError(null);
      setDownloadProgress(0);
      setDownloadRequestId(null);
      downloadRequestIdRef.current = null;
      setCancelPending(false);
      await fetchOfflineMapSize();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to delete offline maps";
      setDeleteError(message);
    } finally {
      setDeletePending(false);
    }
  }, [authorizedFetch, fetchOfflineMapSize, handleClearSelection, setStoredBounds]);

  useEffect(() => {
    if (open) {
      void fetchOfflineMapSize();
    }
  }, [fetchOfflineMapSize, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const id = window.setInterval(() => {
      void fetchOfflineMapSize();
    }, 8000);
    return () => window.clearInterval(id);
  }, [fetchOfflineMapSize, open]);

  useEffect(() => {
    if (downloadStatus === "completed" || downloadStatus === "canceled" || downloadStatus === "error") {
      void fetchOfflineMapSize();
    }
  }, [downloadStatus, fetchOfflineMapSize]);

  useEffect(() => {
    if (importStatus === "completed" || importStatus === "canceled" || importStatus === "error") {
      void fetchOfflineMapSize();
    }
  }, [fetchOfflineMapSize, importStatus]);

  const formatOfflineSize = (value: number | null) => {
    if (value === null || Number.isNaN(value)) {
      return "Unknown";
    }
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    const precision = size >= 10 || unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(precision)} ${units[unitIndex]}`;
  };
  const handleDownloadAction = () => {
    if (!downloadingEnabled) {
      return;
    }
    if (isDownloadRunning) {
      if (!cancelPending) {
        void handleCancelOfflineMapDownload();
      }
    } else {
      void handleOfflineMapDownload();
    }
  };

  const startOfflineMapImport = useCallback(
    async (file: File) => {
      if (!file || importStatus === "running") {
        return;
      }
      const lower = file.name.toLowerCase();
      if (!lower.endsWith(".zip")) {
        setImportError("Please select a .zip archive containing offline map tiles.");
        return;
      }
      const requestId = generateDownloadRequestId();
      setImportRequestId(requestId);
      importRequestIdRef.current = requestId;
      setImportingFileName(file.name);
      setImportStatus("running");
      setImportProgress(1);
      setImportError(null);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("requestId", requestId);
      if (connectionId) {
        formData.append("connectionId", connectionId);
      }
      const controller = new AbortController();
      importAbortControllerRef.current = controller;
      try {
        const response = await authorizedFetch("/api/map/offline/import", {
          method: "POST",
          body: formData,
          signal: controller.signal
        });
        if (!response.ok) {
          const payload = (await response.json().catch(() => null)) as { error?: string } | null;
          const message =
            typeof payload?.error === "string" && payload.error.trim().length > 0
              ? payload.error
              : `Server responded ${response.status}`;
          setImportStatus("error");
          setImportError(message);
          setImportRequestId(null);
          importRequestIdRef.current = null;
          return;
        }
        setImportDialogOpen(false);
        setImportProgress((prev) => (prev > 90 ? prev : 90));
      } catch (error) {
        if (controller.signal.aborted || isAbortError(error)) {
          setImportStatus("canceled");
          setImportError(null);
        } else {
          const message = error instanceof Error ? error.message : "Failed to import offline map";
          setImportStatus("error");
          setImportError(message);
        }
        setImportRequestId(null);
        importRequestIdRef.current = null;
      } finally {
        importAbortControllerRef.current = null;
      }
    },
    [authorizedFetch, connectionId, importStatus]
  );

  const handleImportMap = useCallback(() => {
    setImportStatus("idle");
    setImportProgress(0);
    setImportError(null);
    setImportingFileName(null);
    setImportDialogOpen(true);
    setImportDragActive(false);
    setImportRequestId(null);
    importRequestIdRef.current = null;
  }, []);

  const handleImportFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) {
        return;
      }
      void startOfflineMapImport(file);
    },
    [startOfflineMapImport]
  );

  const handleImportDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setImportDragActive(false);
      if (importStatus === "running") {
        return;
      }
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        void startOfflineMapImport(file);
      }
    },
    [importStatus, startOfflineMapImport]
  );

  const handleImportDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
    if (importStatus !== "running") {
      setImportDragActive(true);
    }
  }, [importStatus]);

  const handleImportDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setImportDragActive(false);
  }, []);

  const handleImportAreaClick = useCallback(() => {
    if (importStatus === "running") {
      return;
    }
    const input = importFileInputRef.current;
    if (input) {
      input.value = "";
      input.click();
    }
  }, [importStatus]);

  const handleCancelOfflineMapImport = useCallback(() => {
    const controller = importAbortControllerRef.current;
    if (controller) {
      controller.abort();
    }
    importAbortControllerRef.current = null;
    if (importStatus !== "running") {
      setImportDialogOpen(false);
      setImportStatus("idle");
      setImportProgress(0);
      setImportError(null);
      setImportingFileName(null);
      setImportRequestId(null);
      setImportDragActive(false);
    }
  }, [importStatus]);

  const handleCancelOfflineMapExport = useCallback(() => {
    const controller = exportAbortControllerRef.current;
    if (controller) {
      controller.abort();
    }
  }, []);

  const handleExportOfflineMaps = useCallback(async () => {
    if (exportAbortControllerRef.current) {
      return;
    }
    // Clear stale download state so progress reflects this export run.
    setDownloadStatus("idle");
    setDownloadProgress(0);
    setDownloadError(null);
    setDownloadRequestId(null);
    downloadRequestIdRef.current = null;
    setExportStatus("running");
    setExportProgress(0);
    setExportError(null);
    setExportTotalBytes(null);

    const controller = new AbortController();
    exportAbortControllerRef.current = controller;

    try {
      const response = await authorizedFetch("/api/map/offline/export", { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`Server responded ${response.status}`);
      }
      const totalBytesHeader = response.headers.get("X-Offline-Map-Total-Bytes");
      const parsedTotal = totalBytesHeader ? Number(totalBytesHeader) : Number.NaN;
      const hasTotal = Number.isFinite(parsedTotal) && parsedTotal > 0;
      setExportTotalBytes(hasTotal ? parsedTotal : null);
      const filename = parseContentDispositionFilename(response.headers.get("Content-Disposition"));

      if (!response.body) {
        const blob = await response.blob();
        triggerBlobDownload(blob, filename);
        setExportProgress(100);
        setExportStatus("completed");
        return;
      }

      const reader = response.body.getReader();
      const chunks: Uint8Array<ArrayBuffer>[] = [];
      let received = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        if (value) {
          chunks.push(value);
          received += value.length;
          if (hasTotal) {
            const percent = Math.min(99, Math.round((received / parsedTotal) * 100));
            setExportProgress(percent);
          } else {
            setExportProgress((prev) => (prev >= 95 ? prev : prev + 2));
          }
        }
      }
      const blob = new Blob(chunks, { type: "application/zip" });
      triggerBlobDownload(blob, filename);
      setExportProgress(100);
      setExportStatus("completed");
    } catch (error) {
      if (controller.signal.aborted || isAbortError(error)) {
        setExportStatus("canceled");
        setExportError(null);
        setExportProgress(0);
      } else {
        const message = error instanceof Error ? error.message : "Failed to export offline maps";
        setExportStatus("error");
        setExportError(message);
      }
    } finally {
      exportAbortControllerRef.current = null;
      setExportTotalBytes(null);
    }
  }, [authorizedFetch]);

  const deleteTooltip = deletePending ? "Deleting offline maps..." : "Delete all offline maps";
  const hasOfflineMapData = offlineSizeBytes === null ? true : offlineSizeBytes > 0;
  const downloadActionTooltip = isDownloadRunning ? "Cancel offline map download" : "Download offline map";
  const downloadActionDisabled =
    isExportRunning ||
    cancelPending ||
    !downloadingEnabled ||
    !hasSelection ||
    !hasLevelSelection ||
    isDrawing ||
    isImportRunning;
  const downloadActionIcon = isDownloadRunning ? <StopCircleIcon fontSize="small" /> : <CloudDownloadIcon fontSize="small" />;
  const exportActionTooltip = isExportRunning ? "Cancel offline map export" : "Export offline map archive";
  const exportActionDisabled =
    isDrawing || deletePending || isDownloadRunning || isImportRunning || (!hasOfflineMapData && !isExportRunning);
  const exportActionIcon = isExportRunning ? <StopCircleIcon fontSize="small" /> : <FileDownloadIcon fontSize="small" />;
  const exportProgressVariant =
    exportStatus === "running" && (!exportTotalBytes || exportTotalBytes <= 0) ? "indeterminate" : "determinate";
  const deleteActionDisabled = deletePending || isDownloadRunning || isExportRunning || isImportRunning || !hasOfflineMapData;
  const progressInfo = useMemo(() => {
    if (importStatus !== "idle") {
      return {
        kind: "import" as const,
        status: importStatus,
        progress: importProgress,
        error: importError,
        canceling: false,
        variant: "determinate" as const
      };
    }
    if (downloadStatus !== "idle") {
      return {
        kind: "download" as const,
        status: downloadStatus,
        progress: downloadProgress,
        error: downloadError,
        canceling: cancelPending,
        variant: "determinate" as const
      };
    }
    if (exportStatus !== "idle") {
      return {
        kind: "export" as const,
        status: exportStatus,
        progress: exportProgress,
        error: exportError,
        canceling: false,
        variant: exportProgressVariant
      };
    }
    return null;
  }, [
    cancelPending,
    downloadError,
    downloadProgress,
    downloadStatus,
    exportError,
    exportProgress,
    exportStatus,
    exportProgressVariant,
    importError,
    importProgress,
    importStatus
  ]);
  const handleExportAction = () => {
    if (isExportRunning) {
      handleCancelOfflineMapExport();
    } else {
      void handleExportOfflineMaps();
    }
  };
  const handleOpenDeleteDialog = () => {
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };
  const handleCloseDeleteDialog = () => {
    if (deletePending) {
      return;
    }
    setDeleteDialogOpen(false);
    setDeleteError(null);
  };

  return (
    <>
      <input
        ref={importFileInputRef}
        type="file"
        accept=".zip,application/zip"
        style={{ display: "none" }}
        onChange={handleImportFileChange}
      />
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        keepMounted
        sx={{
          // enforce a consistent (smaller) font size for dialog content, buttons and labels
          "& .MuiTypography-root": { fontSize: "0.65rem" },
          "& .MuiButton-root": { fontSize: "0.65rem" },
          "& .MuiFormControlLabel-label": { fontSize: "0.65rem" }
        }}
      >
        <DialogTitle sx={{ fontSize: "1.4rem !important", fontWeight: 700 }}>🗺️ Offline maps</DialogTitle>
        <DialogContent>
          {boundsLoadError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {boundsLoadError}
            </Alert>
          )}
          {deleteError && !deleteDialogOpen && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText sx={{ fontSize: "0.65rem", fontStyle: "italic" }}>
            Please check the map provider’s download policy. Use the draw button to select the offline map area.
          </DialogContentText>
          <Box
            sx={{
              mt: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              // border: (theme) => `1px solid ${theme.palette.divider}`,
              // bgcolor: (theme) => theme.palette.action.hover
            }}
          >
            <Tooltip title={downloadActionTooltip}>
              <span>
                <IconButton
                  aria-label={downloadActionTooltip}
                  size="small"
                  onClick={isDownloadRunning ? handleCancelOfflineMapDownload : handleOfflineMapDownload}
                  disabled={downloadActionDisabled}
                >
                  {downloadActionIcon}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={exportActionTooltip}>
              <span>
                <IconButton
                  aria-label={exportActionTooltip}
                  size="small"
                  onClick={handleExportAction}
                  disabled={exportActionDisabled}
                >
                  {exportActionIcon}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Import offline map arhive">
              <span>
                <IconButton
                  aria-label="Import offline map"
                  size="small"
                  onClick={handleImportMap}
                  disabled={isDrawing || isExportRunning || isDownloadRunning || isImportRunning}
                >
                  <FileUploadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={deleteTooltip}>
              <span>
                <IconButton
                  aria-label="Delete all offline maps"
                  size="small"               
                  onClick={handleOpenDeleteDialog}
                  disabled={deleteActionDisabled}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ mt: 0.5, position: "relative" }}>
            <Box
              ref={mapContainerRef}
              sx={{
                height: 360,
                width: "100%",
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                overflow: "hidden"
              }}
            />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.2,
              mb: 0.2,
              fontSize: "0.75rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {selectedBounds
              ? `Selection: lat ${selectedBounds.minLat.toFixed(5)} - ${selectedBounds.maxLat.toFixed(5)}, lon ${selectedBounds.minLon.toFixed(5)} - ${selectedBounds.maxLon.toFixed(5)}`
              : mapViewBounds
                ? `Current map view: lat ${mapViewBounds.minLat.toFixed(5)} - ${mapViewBounds.maxLat.toFixed(5)}, lon ${mapViewBounds.minLon.toFixed(5)} - ${mapViewBounds.maxLon.toFixed(5)}`
                : "No area selected yet. Use the rectangle tool to choose a bounding box."}
            {` | Zoom: ${mapZoom.toFixed(1)}`}
          </Typography>
          <Collapse in={Boolean(selectedBounds)} unmountOnExit sx={{ mt: 1 }}>
            <LevelSelector
              mapZoom={mapZoom}
              selectedLevels={selectedZoomLevels}
              getZoomColor={getZoomColor}
              onChange={handleZoomLevelsChange}
            />
          </Collapse>
          {progressInfo && (
            <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <LinearProgress
                variant={progressInfo.variant as "indeterminate" | "determinate"}
                value={progressInfo.variant === "determinate" ? progressInfo.progress : undefined}
              />
              <Typography
                variant="caption"
                color={progressInfo.status === "error" ? "error" : "text.secondary"}
                sx={{ fontSize: "0.75rem" }}
              >
                {(() => {
                  const label =
                    progressInfo.kind === "download"
                      ? "Offline map download"
                      : progressInfo.kind === "export"
                        ? "Offline map export"
                        : "Offline map import";
                  if (progressInfo.status === "error") {
                    return progressInfo.error ?? `${label} failed`;
                  }
                  if (progressInfo.status === "completed") {
                    return `${label} completed`;
                  }
                  if (progressInfo.status === "canceled") {
                    return `${label} canceled`;
                  }
                  if (progressInfo.canceling) {
                    return `Canceling ${label.toLowerCase()}...`;
                  }
                  return `${label} ${progressInfo.progress}%`;
                })()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ alignItems: "stretch", gap: 1, px: 3, pb: 2 }}>
          <Box
            sx={{
              flex: 1,
              minHeight: 32,
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              borderRadius: 1,
              // border: (theme) => `1px solid ${theme.palette.divider}`,
              // bgcolor: (theme) => theme.palette.action.hover,
              color: "text.secondary"
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "0.8rem" }}>
              Offline maps size:
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, fontSize: "0.65rem" }}>
              {formatOfflineSize(offlineSizeBytes)}
            </Typography>
          </Box>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={importDialogOpen}
        onClose={importStatus === "running" ? undefined : handleCancelOfflineMapImport}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Import offline map</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <DialogContentText sx={{ fontSize: "0.75rem" }}>
            Upload a .zip archive with tiles in the <code>{`{zoom}/{x}/{y}.png`}</code> layout. Existing tiles are kept unless they share
            the same path.
          </DialogContentText>
          {importError && (
            <Alert severity="error" onClose={() => setImportError(null)} sx={{ fontSize: "0.8rem" }}>
              {importError}
            </Alert>
          )}
          <Box
            onClick={handleImportAreaClick}
            onDragOver={handleImportDragOver}
            onDragLeave={handleImportDragLeave}
            onDrop={handleImportDrop}
            sx={{
              p: 2,
              borderRadius: 1,
              border: (theme) => `1px dashed ${theme.palette.primary.main}`,
              bgcolor: (theme) => (importDragActive ? theme.palette.action.hover : theme.palette.background.paper),
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 0.75,
              cursor: importStatus === "running" ? "not-allowed" : "pointer",
              opacity: importStatus === "running" ? 0.65 : 1,
              transition: "background-color 120ms ease"
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {importDragActive ? "Drop the archive to start import" : "Drop the .zip here or click to choose"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
              {importingFileName ? `Selected: ${importingFileName}` : "No files will be removed. Matching tiles will be replaced."}
            </Typography>
          </Box>
          {importStatus !== "idle" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
              <LinearProgress variant="determinate" value={importProgress} />
              <Typography
                variant="caption"
                color={importStatus === "error" ? "error" : "text.secondary"}
                sx={{ fontSize: "0.75rem" }}
              >
                {(() => {
                  if (importStatus === "running") {
                    return `Importing offline map ${importProgress}%`;
                  }
                  if (importStatus === "completed") {
                    return "Offline map import completed";
                  }
                  if (importStatus === "canceled") {
                    return "Offline map import canceled";
                  }
                  return importError ?? "Offline map import failed";
                })()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelOfflineMapImport}>
            {importStatus === "running" ? "Cancel import" : "Close"}
          </Button>
          <Button onClick={handleImportAreaClick} disabled={importStatus === "running"}>
            Choose file
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete offline maps?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.75rem" }}>
            This will remove all downloaded offline map tiles and cancel any ongoing downloads.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deletePending}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleDeleteOfflineMaps()}
            color="error"
            disabled={deletePending}
          >
            {deletePending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OfflineMapCfgDlg;
